import type {
  ConsentMasterModesDocument,
  ConsentMasterModesResponse,
  ConsentResponse,
  TenantConsentMode,
} from '../api/client';

/**
 * 同意画面に出す項目を組み立てる（課題 #77）。
 *
 * 【なぜ必要になったか】
 * 以前は同意項目の表を `GET /v1/consent`（その人の**同意履歴**）から作っていた。
 * まだ一度も同意していない人は履歴が空なので、**同意項目が 1 つも出ず、
 * 同意する手段が画面に無い**状態になっていた。同意するための画面なのに、
 * 同意済みでなければ項目が出ない、という逆立ちした作りだった。
 *
 * 「どの文書に同意が必要か」はテナントの同意モードと公開文書で決まる。
 * その情報は BE の `GET /v1/consent/master-modes` が持っているので、
 * **文書側を起点**にして、そこへ履歴を突き合わせて状態を出す。
 *
 * 同意は法的な意味を持つ。FE が独自の条件で項目を出し入れすると、
 * 「同意を求めたつもりが求めていなかった」が起きうるため、
 * 出す項目の判断は BE が返す文書一覧だけを根拠にする。
 */

/** 画面に出す 1 項目 */
export interface ConsentItem {
  document: ConsentMasterModesDocument;
  /** 同意済みか。履歴が無ければ false（＝これから同意してもらう） */
  granted: boolean;
  /** 対応する履歴。無いこともある */
  record: ConsentResponse | null;
}

/**
 * テナントの同意モードに応じて、同意が必要な文書を選ぶ。
 *
 * `common` は全モード共通（利用規約等）なので常に含める。
 * 個人特定モードのテナントでは匿名モード用の文書も必要になる
 * （個人特定は匿名の上位互換で、匿名集計も引き続き行うため）。
 */
export function selectRequiredDocuments(
  modes: ConsentMasterModesResponse | null | undefined,
  tenantMode: TenantConsentMode | null | undefined
): ConsentMasterModesDocument[] {
  if (!modes) return [];
  const common = modes.common ?? [];
  const anonymous = modes.anonymous ?? [];
  const personal = modes.personal_identified ?? [];
  const docs =
    tenantMode === 'personal_identified'
      ? [...common, ...anonymous, ...personal]
      : [...common, ...anonymous];

  // 同一文書が複数グループに入っていても 1 回だけ出す
  const seen = new Set<string>();
  return docs.filter((d) => {
    if (!d?.id || seen.has(d.id)) return false;
    seen.add(d.id);
    return true;
  });
}

/** 履歴の同一性を判断するキー。document_id が無い古い履歴にも対応する */
function recordKey(r: Pick<ConsentResponse, 'purpose' | 'data_type' | 'version'>): string {
  return `${r.purpose} ${r.data_type} ${r.version}`;
}

/**
 * 文書一覧に同意履歴を突き合わせる。
 *
 * 履歴は同じ文書に対して複数回（granted → revoked → granted）付くため、
 * **最後のイベント**で判断する。revoked のあとに granted が無ければ未同意。
 */
export function buildConsentItems(
  documents: ConsentMasterModesDocument[],
  records: ConsentResponse[] | null | undefined
): ConsentItem[] {
  const list = records ?? [];

  /** 文書 1 件に対応する最後の履歴を返す */
  const latestFor = (doc: ConsentMasterModesDocument): ConsentResponse | null => {
    const matched = list.filter(
      (r) => (r.document_id && r.document_id === doc.id) || recordKey(r) === recordKey(doc)
    );
    if (matched.length === 0) return null;
    // created_at が無い場合は配列順を後ろ優先とみなす
    return matched.reduce((acc, cur) => {
      const a = acc.created_at ?? acc.granted_at ?? acc.revoked_at ?? '';
      const c = cur.created_at ?? cur.granted_at ?? cur.revoked_at ?? '';
      return c >= a ? cur : acc;
    });
  };

  return documents.map((document) => {
    const record = latestFor(document);
    return { document, record, granted: record?.status === 'granted' };
  });
}

/** まだ同意されていない項目があるか */
export function hasUngrantedItems(items: ConsentItem[]): boolean {
  return items.some((i) => !i.granted);
}

/**
 * 目的・データ種別の表示ラベル。
 *
 * BE が `purpose_label_ja` / `data_type_label_ja` を返すのでそれを使う。
 * 以前は FE 側で (purpose, data_type, consent_mode) の組み合わせを決め打ちして
 * 日本語区分に変換していたが、実データと 1 件も一致せず、すべて「その他」に
 * 落ちていた（課題 #77）。コードの追加に FE の改修が要る作りは避ける。
 */
export function documentLabel(doc: ConsentMasterModesDocument): string {
  const purpose = doc.purpose_label_ja || doc.purpose;
  const dataType = doc.data_type_label_ja || doc.data_type;
  if (!dataType) return purpose;
  return `${purpose}（${dataType}）`;
}
